import { useState } from 'react';
import { BellOff } from 'lucide-react';
import { wantsTaskNotifications, type User } from '@peles/shared';
import { useUpdateUser } from '../../hooks/useUsers';

/**
 * A person's own switch for task notifications: hearing, in the app and by
 * email, that a task was given to them or that one they wrote was finished by
 * somebody else. On until they turn it off (`User.taskNotifications`). The
 * daily task email is its own switch, above it.
 */
export function TaskNotificationsSetting({ user }: { user: User }) {
  const updateUser = useUpdateUser();
  const [error, setError] = useState<string | null>(null);
  // The choice on its way to the server, shown until the saved user arrives.
  const [pending, setPending] = useState<boolean | null>(null);
  const blocked = pending ?? !wantsTaskNotifications(user.taskNotifications);

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <label className="flex items-start gap-3 cursor-pointer">
        <input
          type="checkbox"
          className="mt-1 h-4 w-4 rounded border-gray-300 text-primary-600"
          checked={blocked}
          disabled={updateUser.isPending}
          onChange={(e) => {
            const block = e.target.checked;
            setError(null);
            setPending(block);
            updateUser.mutate(
              { id: user.id, updates: { taskNotifications: !block } },
              {
                onError: () => setError('Your choice could not be saved. Try again.'),
                onSettled: () => setPending(null),
              },
            );
          }}
        />
        <span>
          <span className="flex items-center gap-2 text-sm font-medium text-gray-900">
            <BellOff className="h-4 w-4 text-gray-500" aria-hidden="true" />
            Block task notifications
          </span>
          <span className="block text-sm text-gray-500 mt-0.5">
            No notice in the app or by email when a task is assigned to you, or when one you added
            is done.
          </span>
        </span>
      </label>
      {error && (
        <p role="alert" className="mt-2 text-sm text-red-700">
          {error}
        </p>
      )}
    </div>
  );
}
